import React, { useEffect, useState } from "react";
import instance from "../apiInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faTrash, faPen, faGift, faUsers, faCheckCircle, faTimesCircle,
  faBullhorn, faLayerGroup, faCircleCheck, faClock, faArrowUp, faArrowDown,
  faExclamationCircle, faGavel, faClipboardList, faFileLines, faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import "./SurveysManagement.css";
import "./SurveysColeta.css";

const EMPTY_FORM = {
  title: "",
  description: "",
  reward_points: 0,
  target_audience: "all",
  is_active: false,
  recurrence_days: 0,
  context: "geral",
  priority: 0,
  fields: [],
};

const SurveysColeta = () => {
  const [surveys, setSurveys] = useState([]);
  const [spinCount, setSpinCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [incompleteModal, setIncompleteModal] = useState(false);
  const [incompleteUsers, setIncompleteUsers] = useState([]);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [logModal, setLogModal] = useState(false);
  const [logTitle, setLogTitle] = useState("");
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchSurveys = async () => {
    try {
      const res = await instance.get("/admin/surveys");
      setSurveys(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Erro ao carregar pesquisas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
    instance.get("/admin/survey-logs", { params: { survey_id: "roleta" } })
      .then((res) => setSpinCount(res.data?.total ?? 0))
      .catch(() => setSpinCount(0));
  }, []);

  const handleOpenIncomplete = async () => {
    setIncompleteModal(true);
    setIncompleteLoading(true);
    try {
      const res = await instance.get("/admin/surveys/incomplete-users");
      setIncompleteUsers(res.data.users || []);
    } catch (err) {
      toast.error("Erro ao carregar clientes incompletas");
    } finally {
      setIncompleteLoading(false);
    }
  };

  const handleOpenLog = async (surveyId, title) => {
    setLogTitle(title || "Respostas");
    setLogModal(true);
    setLogLoading(true);
    setLogRows([]);
    try {
      const res = await instance.get("/admin/survey-logs", { params: { survey_id: surveyId } });
      setLogRows(res.data?.responses || []);
      if (res.data?.survey?.title) setLogTitle(res.data.survey.title);
    } catch (err) {
      toast.error("Erro ao carregar o log de respostas");
    } finally {
      setLogLoading(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await instance.put(`/admin/surveys/${id}`, { is_active: !currentStatus });
      toast.success(currentStatus ? "Pesquisa desativada" : "Pesquisa ativada");
      fetchSurveys();
    } catch (err) {
      toast.error("Erro ao alterar status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta pesquisa?")) return;
    try {
      await instance.delete(`/admin/surveys/${id}`);
      toast.success("Pesquisa excluída");
      fetchSurveys();
    } catch (err) {
      toast.error("Erro ao excluir");
    }
  };

  const handleEdit = (survey) => {
    setEditingId(survey.id);
    setFormData({
      title: survey.title,
      description: survey.description || "",
      reward_points: survey.reward_points || 0,
      target_audience: survey.target_audience || "all",
      is_active: survey.is_active,
      recurrence_days: survey.recurrence_days || 0,
      context: survey.context || "geral",
      priority: survey.priority || 0,
      fields: survey.fields || [],
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title) return toast.error("Título é obrigatório");
    if (formData.context !== "cadastro" && formData.fields.length === 0) {
      return toast.error("Adicione pelo menos uma pergunta");
    }
    try {
      if (editingId) {
        await instance.put(`/admin/surveys/${editingId}`, formData);
        toast.success("Pesquisa atualizada com sucesso!");
      } else {
        await instance.post("/admin/surveys", formData);
        toast.success("Pesquisa criada com sucesso!");
      }
      setShowModal(false);
      setEditingId(null);
      fetchSurveys();
    } catch (err) {
      toast.error(editingId ? "Erro ao atualizar pesquisa" : "Erro ao criar pesquisa");
    }
  };

  const addField = () => {
    setFormData({
      ...formData,
      fields: [...formData.fields, { name: `field_${Date.now()}`, label: "", type: "text", required: true, options: [] }],
    });
  };

  const updateField = (index, key, value) => {
    const newFields = [...formData.fields];
    newFields[index][key] = value;
    setFormData({ ...formData, fields: newFields });
  };

  const removeField = (index) => {
    const newFields = [...formData.fields];
    newFields.splice(index, 1);
    setFormData({ ...formData, fields: newFields });
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    const newFields = [...formData.fields];
    const temp = newFields[index];
    newFields[index] = newFields[index - 1];
    newFields[index - 1] = temp;
    setFormData({ ...formData, fields: newFields });
  };

  const moveFieldDown = (index) => {
    if (index === formData.fields.length - 1) return;
    const newFields = [...formData.fields];
    const temp = newFields[index];
    newFields[index] = newFields[index + 1];
    newFields[index + 1] = temp;
    setFormData({ ...formData, fields: newFields });
  };

  const getSurveyContext = (survey) => {
    const raw = String(survey?.context || "").trim().toLowerCase();
    if (raw === "cadastro" || raw === "promo" || raw === "geral" || raw === "leilao") return raw;
    if (survey?.target_audience === "incomplete_profile") return "cadastro";
    const title = String(survey?.title || "").toLowerCase();
    if (title.includes("promo") || title.includes("experienc")) return "promo";
    if (title.includes("leil")) return "leilao";
    return "geral";
  };

  const surveysCadastro = surveys.filter((s) => getSurveyContext(s) === "cadastro");
  const surveysPromo = surveys.filter((s) => getSurveyContext(s) === "promo");
  const surveysLeilao = surveys.filter((s) => getSurveyContext(s) === "leilao");
  const surveysGeral = surveys.filter((s) => getSurveyContext(s) === "geral");

  const renderCard = (survey, extra = {}) => {
    const isCadastro = getSurveyContext(survey) === "cadastro";
    const ctx = extra.context || getSurveyContext(survey);
    return (
      <div key={extra.key || survey.id} className="survey-card">
        <div className="survey-card-top">
          <span className={`survey-ctx survey-ctx-${ctx}`}>{extra.badge || ctx}</span>
          {survey.is_active === false && <span className="survey-ctx survey-ctx-off">inativa</span>}
          {ctx === "promo" && <span className="survey-ctx survey-ctx-lab">obrigatório · todas</span>}
          {ctx === "geral" && (survey.title || "").toLowerCase().includes("perfum") && (
            <span className="survey-ctx survey-ctx-lab">obrigatório · pós-cadastro</span>
          )}
          {ctx === "leilao" && <span className="survey-ctx survey-ctx-lab">obrigatório · leilão</span>}
        </div>
        <h3>{survey.title}</h3>
        <p>{survey.description || extra.description || "Sem descrição"}</p>
        <div className="survey-meta">
          <span><FontAwesomeIcon icon={faGift} color="#D4AF37" /> {survey.reward_points || 0} pts</span>
          <span><FontAwesomeIcon icon={faUsers} /> {survey.target_audience === "incomplete_profile" ? "Incompletas" : "Todas"}</span>
          <span>{survey.fields?.length || extra.questions || 0} perguntas</span>
          {survey.recurrence_days > 0 && <span>↻ {survey.recurrence_days}d</span>}
        </div>

        <div className="survey-responses">
          <div
            className="survey-response-stat survey-response-answered survey-response-pending--clickable"
            onClick={() => handleOpenLog(survey.id, survey.title)}
            title="Ver log de respostas"
          >
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>{survey.responses_count ?? extra.responses ?? "—"} responderam</span>
            <span className="survey-pending-hint">ver log →</span>
          </div>
          {extra.hidePending ? null : (
            <div
              className={`survey-response-stat survey-response-pending${isCadastro ? " survey-response-pending--clickable" : ""}`}
              onClick={isCadastro ? handleOpenIncomplete : undefined}
              title={isCadastro ? "Clique para ver quem falta" : undefined}
            >
              <FontAwesomeIcon icon={isCadastro ? faExclamationCircle : faClock} />
              <span>{survey.pending_count ?? "—"} faltam</span>
              {isCadastro && <span className="survey-pending-hint">ver quem →</span>}
            </div>
          )}
        </div>

        {extra.readOnly ? (
          <div className="survey-actions">
            <button className="survey-btn edit" onClick={() => handleOpenLog(survey.id, survey.title)}>
              <FontAwesomeIcon icon={faFileLines} /> Ver respostas
            </button>
          </div>
        ) : (
          <div className="survey-actions">
            <button
              className={`survey-btn ${survey.is_active ? "active" : "inactive"}`}
              onClick={() => handleToggleActive(survey.id, survey.is_active)}
            >
              <FontAwesomeIcon icon={survey.is_active ? faCheckCircle : faTimesCircle} />
              {survey.is_active ? " Ativa" : " Inativa"}
            </button>
            <button className="survey-btn edit" onClick={() => handleEdit(survey)}>
              <FontAwesomeIcon icon={faPen} />
            </button>
            <button className="survey-btn delete" onClick={() => handleDelete(survey.id)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div>Carregando...</div>;

  return (
    <div className="surveys-root">
      <div className="surveys-header">
        <div>
          <h2>Coleta de Dados</h2>
          <p>Todos os questionários criados. Clique em “responderam” para ver o log.</p>
        </div>
        <button className="surveys-btn-new" onClick={() => {
          setEditingId(null);
          setFormData(EMPTY_FORM);
          setShowModal(true);
        }}>
          <FontAwesomeIcon icon={faPlus} /> Nova Coleta
        </button>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faClipboardList} />
          <span>Todos os questionários ({surveys.length + 1})</span>
          <small>Cadastro, promo, leilão, gerais e roleta</small>
        </div>
        <div className="surveys-grid">
          {surveysCadastro.map(renderCard)}
          {surveysPromo.map(renderCard)}
          {surveysLeilao.map(renderCard)}
          {surveysGeral.map(renderCard)}
          {renderCard(
            {
              id: "roleta",
              title: "Pesquisa da Roleta",
              description: "Perguntas antes do giro: o que já ganhou e o que quer.",
              reward_points: 0,
              target_audience: "all",
              is_active: true,
              fields: [{}, {}],
              responses_count: spinCount,
            },
            { key: "roleta-all", context: "roleta", badge: "roleta", hidePending: true, readOnly: true, questions: 2, responses: spinCount },
          )}
          {surveys.length === 0 && <p className="surveys-empty">Nenhuma pesquisa criada.</p>}
        </div>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faUsers} />
          <span>Cadastro incompleto</span>
          <small>Aparece ao logar se faltarem dados</small>
        </div>
        <div className="surveys-grid">
          {surveysCadastro.map(renderCard)}
          {surveysCadastro.length === 0 && <p className="surveys-empty">Nenhuma pesquisa de cadastro criada.</p>}
        </div>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faBullhorn} />
          <span>Promoção</span>
          <small>Gênio / Super promo — no app ainda só a testee vê</small>
        </div>
        <div className="surveys-grid">
          {surveysPromo.map(renderCard)}
          {surveysPromo.length === 0 && <p className="surveys-empty">Nenhuma pesquisa de promo criada.</p>}
        </div>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faGavel} />
          <span>Leilão</span>
          <small>Aparecem na tela de leilão quando o modal existir</small>
        </div>
        <div className="surveys-grid">
          {surveysLeilao.map(renderCard)}
          {surveysLeilao.length === 0 && <p className="surveys-empty">Nenhuma pesquisa de leilão criada.</p>}
        </div>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faLayerGroup} />
          <span>Gerais</span>
          <small>Aparecem no app após X fotos roladas</small>
        </div>
        <div className="surveys-grid">
          {surveysGeral.map(renderCard)}
          {surveysGeral.length === 0 && <p className="surveys-empty">Nenhuma pesquisa geral criada.</p>}
        </div>
      </div>

      <div className="surveys-section">
        <div className="surveys-section-header">
          <FontAwesomeIcon icon={faStar} />
          <span>Roleta</span>
          <small>Pesquisa própria, fora da tabela surveys</small>
        </div>
        <div className="surveys-grid">
          {renderCard(
            {
              id: "roleta",
              title: "Pesquisa da Roleta",
              description: "Já ganhou / quer ganhar, gravado em spin_survey_responses.",
              reward_points: 0,
              target_audience: "all",
              is_active: true,
              fields: [{}, {}],
              responses_count: spinCount,
            },
            { key: "roleta-section", context: "roleta", badge: "roleta", hidePending: true, readOnly: true, questions: 2, responses: spinCount },
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => { setShowModal(false); setEditingId(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Editar Coleta de Dados" : "Nova Coleta de Dados"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="survey-form-group">
            <label>Título do Modal</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Queremos conhecer seu gosto!" />
          </div>
          <div className="survey-form-group">
            <label>Mensagem (Opcional)</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Responda rapidinho e ganhe pontos." rows={2} />
          </div>
          <div className="row">
            <div className="col-md-3 survey-form-group">
              <label>Contexto</label>
              <select value={formData.context} onChange={(e) => setFormData({ ...formData, context: e.target.value })}>
                <option value="geral">Geral (app todo)</option>
                <option value="promo">Promoção</option>
                <option value="leilao">Leilão</option>
                <option value="cadastro">Cadastro Incompleto</option>
              </select>
            </div>
            <div className="col-md-3 survey-form-group">
              <label>Prioridade (maior = 1º)</label>
              <input type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} placeholder="Ex: 10" />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 survey-form-group">
              <label>Pontos de Recompensa</label>
              <input type="number" value={formData.reward_points} onChange={(e) => setFormData({ ...formData, reward_points: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="col-md-4 survey-form-group">
              <label>Público Alvo</label>
              <select value={formData.target_audience} onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}>
                <option value="all">Todas as Clientes</option>
                <option value="incomplete_profile">Apenas Cadastro Incompleto</option>
              </select>
            </div>
            <div className="col-md-4 survey-form-group">
              <label>Recorrência (dias)</label>
              <input type="number" value={formData.recurrence_days} onChange={(e) => setFormData({ ...formData, recurrence_days: parseInt(e.target.value) || 0 })} placeholder="0 = uma vez só" />
              <small style={{ fontSize: 11, color: "#888" }}>0 = uma vez. 7 = a cada 7d.</small>
            </div>
          </div>
          <hr />
          <h5>Perguntas</h5>
          {formData.context === "cadastro" ? (
            <div className="survey-fields-container">
              <p style={{ color: "#666", fontSize: "14px", marginBottom: 0 }}>
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: 8 }} />
                <strong>Atenção:</strong> pesquisa de cadastro incompleto usa as perguntas fixas do sistema.
              </p>
            </div>
          ) : (
            <div className="survey-fields-container">
              {formData.fields.map((field, idx) => (
                <div key={idx} className="survey-field-item">
                  <div className="survey-field-header">
                    <span>Pergunta {idx + 1}</span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button className="btn btn-sm btn-light" onClick={() => moveFieldUp(idx)} disabled={idx === 0} title="Mover para cima"><FontAwesomeIcon icon={faArrowUp} /></button>
                      <button className="btn btn-sm btn-light" onClick={() => moveFieldDown(idx)} disabled={idx === formData.fields.length - 1} title="Mover para baixo"><FontAwesomeIcon icon={faArrowDown} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => removeField(idx)} title="Excluir pergunta"><FontAwesomeIcon icon={faTrash} /></button>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-8 survey-form-group">
                      <label>Qual a pergunta?</label>
                      <input type="text" value={field.label} onChange={(e) => updateField(idx, "label", e.target.value)} placeholder="Ex: Qual seu perfume favorito?" />
                    </div>
                    <div className="col-md-4 survey-form-group">
                      <label>Tipo de Resposta</label>
                      <select value={field.type} onChange={(e) => updateField(idx, "type", e.target.value)}>
                        <option value="text">Texto Livre</option>
                        <option value="select">Lista (Dropdown)</option>
                        <option value="radio">Múltipla Escolha (Botões)</option>
                        <option value="checkbox">Vários (chips)</option>
                      </select>
                    </div>
                  </div>
                  {(field.type === "select" || field.type === "radio" || field.type === "checkbox") && (
                    <div className="survey-form-group">
                      <label>Opções (separadas por vírgula)</label>
                      <input type="text" value={(field.options || []).join(", ")} onChange={(e) => updateField(idx, "options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Ex: Chanel No 5, J'adore, Good Girl" />
                    </div>
                  )}
                  <div className="survey-form-group mb-0">
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, "required", e.target.checked)} style={{ width: "auto" }} />
                      Resposta Obrigatória
                    </label>
                  </div>
                </div>
              ))}
              <button className="survey-btn-add-field" onClick={addField}>
                <FontAwesomeIcon icon={faPlus} /> Adicionar Pergunta
              </button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setEditingId(null); }}>Cancelar</Button>
          <Button variant="primary" style={{ background: "#D4AF37", border: "none" }} onClick={handleSave}>{editingId ? "Salvar Alterações" : "Salvar e Criar"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={incompleteModal} onHide={() => setIncompleteModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "#e67e22", marginRight: 8 }} />
            Clientes com Cadastro Incompleto
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {incompleteLoading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Carregando...</div>
          ) : (
            <>
              <div style={{ padding: "12px 20px", background: "#fff8e1", borderBottom: "1px solid #f0e0a0", fontSize: 13, color: "#8B6914" }}>
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: 6 }} />
                <strong>{incompleteUsers.length}</strong> com cadastro incompleto
                {incompleteUsers.filter((u) => u.accessed_without_completing).length > 0 && (
                  <>
                    {" · "}
                    <strong style={{ color: "#c0392b" }}>
                      {incompleteUsers.filter((u) => u.accessed_without_completing).length}
                    </strong>{" "}
                    acessaram sem completar
                  </>
                )}
              </div>
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <table className="incomplete-users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>E-mail</th>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Dados Faltando</th>
                      <th>Último acesso</th>
                      <th>Tentou acessar</th>
                      <th>Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incompleteUsers.map((u, i) => (
                      <tr key={u.id}>
                        <td style={{ color: "#aaa", fontSize: 12 }}>{i + 1}</td>
                        <td>{u.email || "—"}</td>
                        <td style={{ color: u.name === "indefinido" || !u.name ? "#e74c3c" : "#222" }}>{u.name || "—"}</td>
                        <td style={{ color: u.phone === "indefinido" || !u.phone ? "#e74c3c" : "#222" }}>{u.phone || "—"}</td>
                        <td>{(u.missing_fields || []).map((f) => <span key={f} className="incomplete-badge">{f}</span>)}</td>
                        <td style={{ fontSize: 11, color: u.accessed_without_completing ? "#c0392b" : "#aaa" }}>
                          {u.last_seen ? new Date(u.last_seen).toLocaleString("pt-BR") : "—"}
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {u.accessed_without_completing ? (
                            <>
                              <span className="incomplete-badge" style={{ background: "#fdecea", color: "#c0392b" }}>bloqueada</span>
                              {(u.blocked_pages || []).slice(0, 2).map((p) => (
                                <span key={p} className="incomplete-badge">{p}</span>
                              ))}
                            </>
                          ) : "—"}
                        </td>
                        <td style={{ fontSize: 11, color: "#aaa" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                      </tr>
                    ))}
                    {incompleteUsers.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>Nenhuma cliente com cadastro incompleto</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIncompleteModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={logModal} onHide={() => setLogModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileLines} style={{ marginRight: 8, color: "#B8962E" }} />
            {logTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {logLoading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Carregando log...</div>
          ) : logRows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Ainda ninguém respondeu este questionário.</div>
          ) : (
            <div className="survey-log-list">
              <div className="survey-log-count">{logRows.length} resposta{logRows.length === 1 ? "" : "s"}</div>
              {logRows.map((row) => (
                <article key={row.id} className="survey-log-item">
                  <header>
                    <strong>{row.name && row.name !== "indefinido" ? row.name : "Sem nome"}</strong>
                    <span>{row.completed_at_label || "—"}</span>
                  </header>
                  <p>{row.email || "—"}{row.phone ? ` · ${row.phone}` : ""}</p>
                  <dl>
                    {Object.entries(row.answers || {}).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{value || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLogModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveysColeta;
